/**
 * Shared OTP-challenge persistence utilities.
 *
 * Used by StaffOtpGate (admin login) and CustomerOtpGate (storefront signup)
 * so the active OTP challenge survives:
 *   • component unmounts (route changes to read the email tab and back)
 *   • tab switches and brief inactivity
 *   • full page refreshes inside the OTP validity window
 *
 * State is stored in localStorage under a per-(scope,email,userId) key with
 * a `sentAt` and `expiresAt` (ms). Resending only happens when there is no
 * unexpired challenge — preventing the historical bug where reloading the
 * gate immediately invalidated the previous code by issuing a new one.
 */

export const OTP_VALIDITY_MS = 5 * 60 * 1000;
export const OTP_RESEND_COOLDOWN_SEC = 45;

type Scope = "staff" | "customer-signup";

function key(scope: Scope, email: string, userId?: string) {
  const u = userId ? `${userId}:` : "";
  return `khays_otp_challenge:${scope}:${u}${email.trim().toLowerCase()}`;
}

export interface OtpChallenge {
  sentAt: number;
  expiresAt: number;
}

export function readChallenge(scope: Scope, email: string, userId?: string): OtpChallenge | null {
  if (!email) return null;
  try {
    const raw = localStorage.getItem(key(scope, email, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.expiresAt !== "number" || parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(key(scope, email, userId));
      return null;
    }
    return parsed as OtpChallenge;
  } catch {
    return null;
  }
}

export function saveChallenge(scope: Scope, email: string, userId?: string): OtpChallenge {
  const now = Date.now();
  const challenge: OtpChallenge = { sentAt: now, expiresAt: now + OTP_VALIDITY_MS };
  try { localStorage.setItem(key(scope, email, userId), JSON.stringify(challenge)); } catch { /* ignore */ }
  return challenge;
}

export function clearChallenge(scope: Scope, email: string, userId?: string) {
  try { localStorage.removeItem(key(scope, email, userId)); } catch { /* ignore */ }
}

export function formatMmSs(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export function isExpiredOtpError(message: string): boolean {
  const m = (message || "").toLowerCase();
  return (
    m.includes("expired") ||
    m.includes("invalid") ||
    m.includes("not found") ||
    m.includes("token has") ||
    m.includes("otp_expired")
  );
}
